import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BankAccountCompany,
  BankAccountRecord,
} from '@interfaces/bank-accounts.interface';
import { BeneficiaryType } from '@constants/constants';
import { DeleteProviderComponent } from '@shared/mat-dialogs/delete-provider/delete-provider.component';
import { AlertService } from '@services/alert.service';
import { ApiService } from '@services/api.service';
import { ParamsService } from '@services/params.service';
import { DeleteBankAccountComponent } from '@shared/mat-dialogs/delete-bank-account/delete-bank-account.component';
import { EditBankAccountComponent } from '@shared/mat-dialogs/edit-bank-account/edit-bank-account.component';
import { RequestProviderComponent } from '@shared/mat-dialogs/request-provider/request-provider.component';
import { WithdrawMoneyComponent } from '@shared/mat-dialogs/withdraw-money/withdraw-money.component';
import { DataResponse } from '@shared/mat-table/table.interface';
import { SubSink } from 'subsink';

@Component({
  selector: 'ew-providers',
  templateUrl: './providers.component.html',
})
export class ProvidersComponent implements OnInit, OnDestroy {
  open: boolean = false;
  bankAccount?: BankAccountCompany;
  beneficiary: string = BeneficiaryType.EXTERNAL_PROVIDER;

  bankAccounts?: DataResponse<BankAccountRecord>;
  bankAccountCompanies: BankAccountCompany[] = [];
  columns: string[] = [
    'alias',
    'bankName',
    'accountNumber',
    'country',
    'status',
    'actions',
  ];

  selected: any = null;
  masterAccount: any = null;
  loading: boolean = false;

  tableDataProviders: DataResponse<BankAccountCompany> = {
    ...this.bankAccounts,
    records: this.bankAccountCompanies,
    success: this.bankAccounts?.success ?? false,
  };

  static DEFAULT_LOGO =
    'https://cdn-icons-png.flaticon.com/512/5345/5345937.png';
  get DEFAULT_LOGO() {
    return ProvidersComponent.DEFAULT_LOGO;
  }

  private sub = new SubSink();

  constructor(
    private API: ApiService,
    private PRMS: ParamsService,
    private DIALOG: MatDialog,
    private ALERT: AlertService,
  ) {}

  ngOnInit(): void {
    this.sub.sink = this.PRMS.get.subscribe(() => {
      this.searchProviders();
    });
  }

  searchProviders() {
    this.loading = true;
    this.API.get(
      `v1/bank-accounts?beneficiary=${this.beneficiary}`,
      this.PRMS.params,
    ).subscribe((data: DataResponse) => {
      this.bankAccounts = data;
      this.buildRows();
      this.loading = false;
    });
  }

  buildRows() {
    const rows: BankAccountCompany[] = [];
    this.bankAccounts?.records.forEach((bankAccount) => {
      bankAccount.bankAccountCompanies.forEach((bankAccountCompany) => {
        const row = { ...bankAccountCompany, bankAccount };
        rows.push(row);
      });
    });

    this.tableDataProviders = {
      records: rows,
      success: this.bankAccounts?.success ?? false,
      meta: this.bankAccounts?.meta,
    };
  }

  requestNewProvider(provider: any) {
    this.DIALOG.open(RequestProviderComponent, {
      width: '65vw',
      panelClass: 'sm-full',
      data: provider,
    });
  }

  sendPayment(bankAccount: any) {
    this.DIALOG.open(WithdrawMoneyComponent, {
      width: '95vw',
      height: '95vh',
      panelClass: 'sm-full',
      data: {
        providerBankAccount: bankAccount,
        type: 'PAYMENT',
      },
    });
  }

  deleteBeneficiaryAccount(bankAccountId: string, alias: string): void {
    this.DIALOG.open(DeleteProviderComponent, {
      width: '95vw',
      height: '75vh',
      panelClass: 'sm-full',
      data: {
        bankAccountId,
        alias,
      },
    });
  }

  editBankAccount(bankAccount: any): void {
    const dialogRef = this.DIALOG.open(EditBankAccountComponent, {
      width: '60vw',
      panelClass: 'sm-full',
      data: {
        bankAccount,
      },
    });

    dialogRef.componentInstance.updated.subscribe(() => {
      this.searchProviders();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
